import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

// Scope-Typen gemäß neuer Spezifikation
export type Scope = 
  | "masterdata"
  | "job_description"
  | "documents"
  | "time_absence"
  | "equipment"
  | "payroll"
  | "other_costs"
  | "ratings"
  | "credentials"
  | "kfz_images"
  | "colleagues_directory"
  | "work_processes"
  | "help";

export type PermissionType = "read" | "write" | "delete";

export interface ScopePermission {
  scope: string;
  can_read: boolean;
  can_write: boolean;
  can_delete: boolean;
}

export interface DocumentCategoryPermission {
  category_id: string;
  category_name: string;
  can_read: boolean;
  can_upload: boolean;
  can_delete: boolean;
}

// Deutsche Rollennamen für UI-Anzeige
export const ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  hr_manager: "Personalmanager",
  hr_clerk: "Personalverwalter",
  employee: "Mitarbeiter",
};

// Rollen (ohne vorarbeiter)
export const ROLES = ["admin", "hr_manager", "hr_clerk", "employee"] as const;
export type AppRole = (typeof ROLES)[number];

// Eingeschränkte Dokumenten-Kategorien für hr_clerk
// Diese Kategorien werden für Personalverwalter komplett ausgeblendet
export const HR_CLERK_RESTRICTED_CATEGORIES = [
  "Arbeitsverträge",
  "Vertragsänderungen", 
  "Abmahnungen",
  "Lohnunterlagen",
] as const;

// SELF-Berechtigungen (fest, nicht konfigurierbar)
const SELF_PERMISSIONS: Record<Scope, { read: boolean; write: boolean; delete: boolean }> = {
  masterdata: { read: true, write: false, delete: false },
  job_description: { read: true, write: false, delete: false },
  documents: { read: true, write: true, delete: false }, // Upload eigener Dokumente erlaubt
  time_absence: { read: true, write: false, delete: false },
  equipment: { read: true, write: false, delete: false },
  payroll: { read: true, write: false, delete: false },
  other_costs: { read: true, write: false, delete: false },
  ratings: { read: true, write: false, delete: false },
  credentials: { read: true, write: true, delete: false }, // Passwort-Self-Service erlaubt
  kfz_images: { read: true, write: true, delete: false }, // KFZ-Bilder Upload erlaubt
  colleagues_directory: { read: true, write: false, delete: false },
  work_processes: { read: true, write: false, delete: false },
  help: { read: true, write: false, delete: false },
};
// Feste Berechtigungen pro Rolle (nicht konfigurierbar)
const ROLE_PERMISSIONS: Record<string, Record<Scope, { read: boolean; write: boolean; delete: boolean }>> = {
  admin: {
    masterdata: { read: true, write: true, delete: true },
    job_description: { read: true, write: true, delete: true },
    documents: { read: true, write: true, delete: true },
    time_absence: { read: true, write: true, delete: true },
    equipment: { read: true, write: true, delete: true },
    payroll: { read: true, write: true, delete: true },
    other_costs: { read: true, write: true, delete: true },
    ratings: { read: true, write: true, delete: true },
    credentials: { read: true, write: true, delete: true },
    kfz_images: { read: true, write: true, delete: true },
    colleagues_directory: { read: true, write: true, delete: true },
    work_processes: { read: true, write: true, delete: true },
    help: { read: true, write: true, delete: true },
  },
  hr_manager: {
    masterdata: { read: true, write: true, delete: false },
    job_description: { read: true, write: true, delete: false },
    documents: { read: true, write: true, delete: true },
    time_absence: { read: true, write: true, delete: false },
    equipment: { read: true, write: true, delete: true },
    payroll: { read: true, write: true, delete: true },
    other_costs: { read: true, write: true, delete: true },
    ratings: { read: true, write: true, delete: true },
    credentials: { read: true, write: true, delete: false },
    kfz_images: { read: true, write: true, delete: true },
    colleagues_directory: { read: true, write: false, delete: false },
    work_processes: { read: true, write: true, delete: false },
    help: { read: true, write: false, delete: false },
  },
  hr_clerk: {
    masterdata: { read: true, write: true, delete: false },
    job_description: { read: true, write: true, delete: false },
    documents: { read: true, write: true, delete: false },
    time_absence: { read: true, write: true, delete: false },
    equipment: { read: true, write: true, delete: false },
    payroll: { read: false, write: false, delete: false }, // KEIN Zugriff
    other_costs: { read: true, write: true, delete: false },
    ratings: { read: true, write: false, delete: false }, // Nur lesen
    credentials: { read: true, write: true, delete: false },
    kfz_images: { read: true, write: true, delete: false },
    colleagues_directory: { read: true, write: false, delete: false },
    work_processes: { read: true, write: false, delete: false },
    help: { read: true, write: false, delete: false },
  },
  employee: {
    masterdata: { read: false, write: false, delete: false },
    job_description: { read: false, write: false, delete: false },
    documents: { read: false, write: false, delete: false },
    time_absence: { read: false, write: false, delete: false },
    equipment: { read: false, write: false, delete: false },
    payroll: { read: false, write: false, delete: false },
    other_costs: { read: false, write: false, delete: false },
    ratings: { read: false, write: false, delete: false },
    credentials: { read: false, write: false, delete: false },
    kfz_images: { read: false, write: false, delete: false },
    colleagues_directory: { read: true, write: false, delete: false },
    work_processes: { read: true, write: false, delete: false },
    help: { read: true, write: false, delete: false },
  },
};

export const usePermissions = () => {
  const { user, userRole } = useAuth();
  const [documentPermissions, setDocumentPermissions] = useState<DocumentCategoryPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [ownEmployeeId, setOwnEmployeeId] = useState<string | null>(null);

  // Prüft ob User Admin ist
  const isAdmin = useMemo(() => userRole === "admin", [userRole]);

  // Lädt die eigene Employee-ID
  const fetchOwnEmployeeId = useCallback(async () => {
    if (!user) return null;
    
    const { data } = await supabase
      .from("profiles")
      .select("employee_id")
      .eq("id", user.id)
      .single();
    
    return data?.employee_id || null;
  }, [user]);

  // Berechnet OTHERS-Berechtigungen basierend auf festen Rollendefinitionen
  const othersPermissions = useMemo((): Record<string, ScopePermission> => {
    if (!userRole) return {};
    
    const rolePerms = ROLE_PERMISSIONS[userRole];
    if (!rolePerms) return {};
    
    const permMap: Record<string, ScopePermission> = {};
    Object.entries(rolePerms).forEach(([scope, perms]) => {
      permMap[scope] = {
        scope,
        can_read: perms.read,
        can_write: perms.write,
        can_delete: perms.delete,
      };
    });
    
    return permMap;
  }, [userRole]);

  // Lädt Dokumenten-Kategorie-Berechtigungen
  const fetchDocumentPermissions = useCallback(async () => {
    if (!user) {
      setDocumentPermissions([]);
      return;
    }

    try {
      const { data, error } = await supabase.rpc("get_my_document_permissions");
      
      if (error) throw error;

      setDocumentPermissions(data || []);
    } catch (error) {
      console.error("Error fetching document permissions:", error);
      setDocumentPermissions([]);
    }
  }, [user]);

  // Initial laden
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const empId = await fetchOwnEmployeeId();
      setOwnEmployeeId(empId);
      await fetchDocumentPermissions();
      setLoading(false);
    };
    
    loadAll();
  }, [fetchOwnEmployeeId, fetchDocumentPermissions]);

  /**
   * ZENTRALE BERECHTIGUNGSPRÜFUNG
   * 
   * @param scope - Der zu prüfende Bereich (z.B. "payroll", "masterdata")
   * @param permission - Die Art der Berechtigung ("read", "write", "delete")
   * @param targetEmployeeId - Optional: Die ID des Mitarbeiters, auf den zugegriffen wird
   * @returns boolean - Ob die Berechtigung besteht
   */
  const checkPermission = useCallback(
    (
      scope: Scope,
      permission: PermissionType = "read",
      targetEmployeeId?: string
    ): boolean => {
      // ADMIN: Alles erlaubt, keine Einschränkungen
      if (isAdmin) return true;

      // Prüfe ob SELF-Kontext (eigene Daten)
      const isSelf = targetEmployeeId !== undefined && ownEmployeeId === targetEmployeeId;

      if (isSelf) {
        // SELF: Feste Berechtigungen, nicht konfigurierbar
        const selfPerm = SELF_PERMISSIONS[scope];
        if (!selfPerm) return false;

        switch (permission) {
          case "read":
            return selfPerm.read;
          case "write":
            return selfPerm.write;
          case "delete":
            return selfPerm.delete;
          default:
            return false;
        }
      }

      // Globale Bereiche (ohne targetEmployeeId) - für employee immer lesbar
      if (targetEmployeeId === undefined) {
        if (["colleagues_directory", "work_processes", "help"].includes(scope) && permission === "read") {
          return true;
        }
      }

      // OTHERS: Rollenmatrix prüfen
      const perm = othersPermissions[scope];
      if (!perm) return false;

      switch (permission) {
        case "read":
          return perm.can_read;
        case "write":
          return perm.can_write;
        case "delete":
          return perm.can_delete;
        default:
          return false;
      }
    },
    [isAdmin, ownEmployeeId, othersPermissions]
  );

  /**
   * Prüft Dokumenten-Kategorie-Berechtigung
   */
  const checkDocumentCategoryPermission = useCallback(
    (
      categoryId: string,
      permission: "read" | "upload" | "delete",
      targetEmployeeId?: string
    ): boolean => {
      // ADMIN: Alles erlaubt
      if (isAdmin) return true;

      // SELF: Eigene Dokumente
      const isSelf = targetEmployeeId !== undefined && ownEmployeeId === targetEmployeeId;
      if (isSelf) {
        if (permission === "read") return true;
        if (permission === "upload") return true;
        return false; // Delete eigener Dokumente verboten
      }

      // OTHERS: Erst allgemeines Dokumentenrecht prüfen
      if (!checkPermission("documents", permission === "upload" ? "write" : permission, targetEmployeeId)) {
        return false;
      }

      // Dann Kategorie-Berechtigung prüfen
      const catPerm = documentPermissions.find((p) => p.category_id === categoryId);
      if (!catPerm) return false;

      switch (permission) {
        case "read":
          return catPerm.can_read;
        case "upload":
          return catPerm.can_upload;
        case "delete":
          return catPerm.can_delete;
        default:
          return false;
      }
    },
    [isAdmin, ownEmployeeId, documentPermissions, checkPermission]
  );

  /**
   * Prüft ob es sich um eigene Daten handelt
   */
  const isSelfContext = useCallback(
    (targetEmployeeId: string | undefined): boolean => {
      if (!targetEmployeeId || !ownEmployeeId) return false;
      return targetEmployeeId === ownEmployeeId;
    },
    [ownEmployeeId]
  );

  /**
   * Holt alle Kategorien mit Upload-Berechtigung
   */
  const getUploadableCategories = useCallback(
    (targetEmployeeId?: string): DocumentCategoryPermission[] => {
      if (isAdmin) return documentPermissions;

      const isSelf = targetEmployeeId !== undefined && ownEmployeeId === targetEmployeeId;
      
      // hr_clerk: Eingeschränkte Kategorien ausfiltern
      let filteredCategories = documentPermissions;
      if (userRole === "hr_clerk") {
        filteredCategories = documentPermissions.filter(
          (p) => !HR_CLERK_RESTRICTED_CATEGORIES.includes(p.category_name as any)
        );
      }
      
      if (isSelf) {
        // SELF: Alle (gefilterten) Kategorien für Upload verfügbar
        return filteredCategories;
      }

      // OTHERS: Nur Kategorien mit can_upload
      return filteredCategories.filter((p) => p.can_upload);
    },
    [isAdmin, ownEmployeeId, documentPermissions, userRole]
  );

  /**
   * Holt alle Kategorien mit Lese-Berechtigung
   */
  const getReadableCategories = useCallback(
    (targetEmployeeId?: string): DocumentCategoryPermission[] => {
      if (isAdmin) return documentPermissions;

      const isSelf = targetEmployeeId !== undefined && ownEmployeeId === targetEmployeeId;
      
      // hr_clerk: Eingeschränkte Kategorien ausfiltern
      let filteredCategories = documentPermissions;
      if (userRole === "hr_clerk") {
        filteredCategories = documentPermissions.filter(
          (p) => !HR_CLERK_RESTRICTED_CATEGORIES.includes(p.category_name as any)
        );
      }
      
      if (isSelf) {
        // SELF: Alle (gefilterten) Kategorien lesbar
        return filteredCategories;
      }

      // OTHERS: Nur Kategorien mit can_read
      return filteredCategories.filter((p) => p.can_read);
    },
    [isAdmin, ownEmployeeId, documentPermissions, userRole]
  );

  // Legacy-Kompatibilität: canRead, canEdit, canDelete
  const canRead = useCallback(
    (scope: string, targetEmployeeId?: string): boolean => {
      return checkPermission(scope as Scope, "read", targetEmployeeId);
    },
    [checkPermission]
  );

  const canEdit = useCallback(
    (scope: string, targetEmployeeId?: string): boolean => {
      return checkPermission(scope as Scope, "write", targetEmployeeId);
    },
    [checkPermission]
  );

  const canDelete = useCallback(
    (scope: string, targetEmployeeId?: string): boolean => {
      return checkPermission(scope as Scope, "delete", targetEmployeeId);
    },
    [checkPermission]
  );

  return useMemo(
    () => ({
      // Core
      loading,
      isAdmin,
      ownEmployeeId,
      
      // Zentrale Prüfung
      checkPermission,
      checkDocumentCategoryPermission,
      isSelfContext,
      
      // Dokumente
      documentPermissions,
      getUploadableCategories,
      getReadableCategories,
      
      // Legacy-Kompatibilität
      canRead,
      canEdit,
      canDelete,
      
      // Refetch
      refetch: async () => {
        await fetchDocumentPermissions();
      },
    }),
    [
      loading,
      isAdmin,
      ownEmployeeId,
      checkPermission,
      checkDocumentCategoryPermission,
      isSelfContext,
      documentPermissions,
      getUploadableCategories,
      getReadableCategories,
      canRead,
      canEdit,
      canDelete,
      fetchDocumentPermissions,
    ]
  );
};

// Typ-Definition für Navigations-Items
export interface NavItem {
  path: string;
  label: string;
  icon: string;
  scope?: Scope;
  alwaysVisible?: boolean;
}

// Navigation-Konfiguration basierend auf Scopes
export const getNavigationConfig = (): NavItem[] => [
  { path: "/", label: "Dashboard", icon: "BarChart3", scope: undefined }, // Nur für Admin/HR sichtbar (handled in Navigation)
  { path: "/employees", label: "Mitarbeiter", icon: "Users", scope: "masterdata" },
  { path: "/directory", label: "Kollegenverzeichnis", icon: "BookUser", scope: "colleagues_directory", alwaysVisible: true },
  { path: "/applicants", label: "Bewerber", icon: "UserPlus", scope: undefined }, // Recruiting-spezifisch
  { path: "/orgchart", label: "Organigramm", icon: "Network", scope: undefined },
  { path: "/work-processes", label: "Arbeitsprozesse", icon: "GitBranch", scope: "work_processes", alwaysVisible: true },
  { path: "/cost-overview", label: "Einstandslöhne", icon: "Calculator", scope: "payroll" },
  { path: "/matrix", label: "Bewertungsmatrix", icon: "Grid3x3", scope: "ratings" },
  { path: "/settings", label: "Einstellungen", icon: "Settings", scope: undefined },
  { path: "/help", label: "Hilfe", icon: "HelpCircle", scope: "help", alwaysVisible: true },
];

// Mapping von Tab-Werten zu Scopes
export const TAB_SCOPE_MAP: Record<string, Scope> = {
  masterdata: "masterdata",
  jobdesc: "job_description",
  documents: "documents",
  timetracking: "time_absence",
  clothing: "equipment",
  tools: "equipment",
  "mobile-phone": "equipment",
  salary: "payroll",
  bonus: "payroll",
  benefits: "payroll",
  "vehicle-tasks": "kfz_images",
  "vehicle-costs": "other_costs",
  "ev-charging": "other_costs",
  "other-costs": "other_costs",
  evaluations: "ratings",
  perks: "payroll",
  credentials: "credentials",
};
